// 着席と近接効果の積分（v0.7.0）。来店した客が「どの席に座るか」を決め、その席の
// まわりの庭デコの効果を距離重み付きで合算する純ロジック。すべて決定的（乱数なし）。
//
// ユーザー要望の核: 客は席に座って飲む。席のまわりのマスの効果を「積分」するように受け、
// 良いデコで囲まれた席ほどチップが増える。
//
// 設計:
//  - 席 = bench / cafe_table（DecoDef.seat=true）。来店成立時に「空いている席のうち
//    近接アンビエンスが最大の席」を決定的に選ぶ（同点は cellIndex で安定タイブレーク）。
//  - 近接アンビエンス = 席セルを中心に半径 R 内の各セルの実効属性寄与を距離重み付きで合算
//    （近いほど重い）。ゆらぎ（しおれ）・成長・シナジー・スペシャルの実効属性を尊重するため、
//    呼び出し側が「セル index → 実効属性合計」を返す関数（cellAmbianceContribution）を渡す。
//  - チップ反映: tip = baseTip × (1 + AMBIANCE_K × ambiance)、上限 AMBIANCE_TIP_CAP。
//
// 決定的・乱数なしなのでオフライン精算でも効く。simulate がこれを呼ぶ。
//
// 多島化（次バージョン）を見据え、state ではなく (garden, ...) を引数に取る。
//
// この層は描画・DOM・乱数に触れない純データ＋純関数。

import type { GardenGrid } from './garden';
import { GRID_SIZE, cellIndex, cellLevel } from './garden';
import { getDeco, isSeatDeco, leveledAttributes, ATTRIBUTE_KEYS, type AttributeKey } from './decos';

/** 近接アンビエンスの積分半径（セル。R=2 → 5×5 の窓を距離重みで合算）。 */
export const AMBIANCE_RADIUS = 2;

/**
 * チップへの反映係数と上限。tip = baseTip × (1 + AMBIANCE_K × ambiance)。
 * ambiance は「席まわりの距離重み付き属性合計」（おおむね 0..十数）。cozy 方針なので
 * 係数は控えめ、ボーナスは最大でも基礎チップの AMBIANCE_TIP_CAP 倍（×2）に頭打ち。
 */
export const AMBIANCE_K = 0.12;
export const AMBIANCE_TIP_CAP = 1.0; // ボーナス分の上限（基礎チップに対する倍率）。

/**
 * セル index → そのセルの「アンビエンスへの寄与スコア」を返す関数の型。
 *
 * 呼び出し側（simulate / 表示）が、ゆらぎ・成長・スペシャル等を反映した実効属性から
 * 1 つのスカラー（属性値の総和など）を作って渡す。seat 層は距離重みを掛けて積分するだけ。
 * 席セル自身・空セルは 0 を返してよい。
 */
export type CellContribution = (cellIndex: number) => number;

/**
 * チェビシェフ距離 d（0..R）に対する距離重み。近いほど重い。
 *  d=0（席自身、ただし通常 0 寄与）→ 1.0 / d=1 → 0.6 / d=2 → 0.3。線形に逓減。
 */
function distanceWeight(d: number): number {
  if (d <= 0) return 1.0;
  if (d >= AMBIANCE_RADIUS) return Math.max(0, 1 - AMBIANCE_RADIUS * 0.35);
  return 1 - d * 0.35;
}

/**
 * 席セル（seatIdx）を中心に半径 R 内のセル寄与を距離重み付きで合算した
 * 「アンビエンススコア」を返す純関数。近いほど重い（distanceWeight）。
 *
 * contribution は呼び出し側が用意した「セル index → 実効属性スカラー」。これにより
 * ゆらぎ・成長・シナジー・スペシャルの実効属性を尊重できる（seat 層は距離だけ担当）。
 *
 * @param seatIdx      席セルの index。
 * @param contribution セル index → 寄与スカラー（実効属性合計など）。
 */
export function seatAmbiance(seatIdx: number, contribution: CellContribution): number {
  const sgx = seatIdx % GRID_SIZE;
  const sgy = Math.floor(seatIdx / GRID_SIZE);
  let total = 0;
  for (let dy = -AMBIANCE_RADIUS; dy <= AMBIANCE_RADIUS; dy++) {
    for (let dx = -AMBIANCE_RADIUS; dx <= AMBIANCE_RADIUS; dx++) {
      const gx = sgx + dx;
      const gy = sgy + dy;
      if (gx < 0 || gx >= GRID_SIZE || gy < 0 || gy >= GRID_SIZE) continue;
      if (dx === 0 && dy === 0) continue; // 席自身は寄与に数えない（まわりを積分）
      const d = Math.max(Math.abs(dx), Math.abs(dy)); // チェビシェフ距離
      const w = distanceWeight(d);
      if (w <= 0) continue;
      total += w * contribution(cellIndex(gx, gy));
    }
  }
  return total;
}

/**
 * セルの「実効属性スカラー」を、ゆらぎ込みの実効属性ベクトルから作るデフォルト寄与関数を返す。
 *
 * 各セルの (デコ基礎属性 × level 倍率 × 活力) の全属性合計を寄与とする。植物の成長ぶんは
 * ここでは数えない（席まわりの「飾りの密度・質」を測る指標にする）。ゆらぎ（活力）は
 * 呼び出し側が vitality(cellIndex)→0..1 を渡して反映する。
 *
 * @param garden    庭グリッド。
 * @param vitality  セル index → 活力(0..1)。省略時は常に 1.0。
 */
export function makeCellContribution(
  garden: GardenGrid,
  vitality?: (cellIndex: number) => number,
): CellContribution {
  return (idx: number): number => {
    const cell = garden[idx];
    if (!cell) return 0;
    const deco = getDeco(cell.decoId);
    if (!deco) return 0;
    const leveled = leveledAttributes(deco.attributes, cellLevel(cell));
    const vit = vitality ? vitality(idx) : 1.0;
    let sum = 0;
    for (const key of ATTRIBUTE_KEYS) {
      const v = leveled[key];
      if (v != null) sum += v;
    }
    return sum * (vit < 1 ? vit : 1);
  };
}

/** 庭にある空き席の index を昇順で返す（占有判定は呼び出し側が occupied で渡す）。 */
export function freeSeats(garden: GardenGrid, occupied: ReadonlySet<number>): number[] {
  const out: number[] = [];
  for (let idx = 0; idx < garden.length; idx++) {
    const cell = garden[idx];
    if (cell && isSeatDeco(cell.decoId) && !occupied.has(idx)) out.push(idx);
  }
  return out;
}

/**
 * 来店成立時の着席先を決定的に選ぶ純関数。
 *
 * 空いている席のうち seatAmbiance が最大のものを選ぶ。同点は cellIndex の小さい方
 * （安定タイブレーク）。席が無ければ null（= 立ち飲み。アンビエンスボーナスなし）。
 *
 * @param garden       庭グリッド。
 * @param occupied     既に埋まっている席 index の集合（同刻みの複数来店で増える）。
 * @param contribution セル index → 寄与スカラー（makeCellContribution 等）。
 * @returns 選んだ席 index と そのアンビエンス。席なしは null。
 */
export function chooseSeat(
  garden: GardenGrid,
  occupied: ReadonlySet<number>,
  contribution: CellContribution,
): { seatIndex: number; ambiance: number } | null {
  const seats = freeSeats(garden, occupied);
  if (seats.length === 0) return null;
  let bestIdx = seats[0];
  let bestAmb = seatAmbiance(seats[0], contribution);
  for (let i = 1; i < seats.length; i++) {
    const amb = seatAmbiance(seats[i], contribution);
    // 厳密に大きいときだけ更新（同点は先に評価した小さい index を保持＝安定タイブレーク）。
    if (amb > bestAmb + 1e-9) {
      bestAmb = amb;
      bestIdx = seats[i];
    }
  }
  return { seatIndex: bestIdx, ambiance: bestAmb };
}

/**
 * アンビエンスからチップ倍率（baseTip に掛ける）を返す。1.0 + AMBIANCE_K×ambiance を
 * [1, 1+AMBIANCE_TIP_CAP] にクランプ。席なし（ambiance=0）なら 1.0（ボーナスなし）。
 */
export function ambianceTipMultiplier(ambiance: number): number {
  const raw = 1 + AMBIANCE_K * Math.max(0, ambiance);
  const cap = 1 + AMBIANCE_TIP_CAP;
  return raw > cap ? cap : raw;
}

/**
 * 席まわりの「効いている属性」内訳（後段 UI 用、任意）。半径 R 内の距離重み付き属性ごとの
 * 合算を返す。どの属性がアンビエンスに効いたかを示せる。simulate では使わないが、
 * presentation / ステータス表示が席の魅力を説明するのに使える。
 */
export function seatAmbianceBreakdown(
  garden: GardenGrid,
  seatIdx: number,
  vitality?: (cellIndex: number) => number,
): Record<AttributeKey, number> {
  const out: Record<AttributeKey, number> = {
    light: 0, scent: 0, water: 0, calm: 0, lively: 0, crystal: 0, green: 0,
  };
  const sgx = seatIdx % GRID_SIZE;
  const sgy = Math.floor(seatIdx / GRID_SIZE);
  for (let dy = -AMBIANCE_RADIUS; dy <= AMBIANCE_RADIUS; dy++) {
    for (let dx = -AMBIANCE_RADIUS; dx <= AMBIANCE_RADIUS; dx++) {
      const gx = sgx + dx;
      const gy = sgy + dy;
      if (gx < 0 || gx >= GRID_SIZE || gy < 0 || gy >= GRID_SIZE) continue;
      if (dx === 0 && dy === 0) continue;
      const d = Math.max(Math.abs(dx), Math.abs(dy));
      const w = distanceWeight(d);
      if (w <= 0) continue;
      const cell = garden[cellIndex(gx, gy)];
      if (!cell) continue;
      const deco = getDeco(cell.decoId);
      if (!deco) continue;
      const leveled = leveledAttributes(deco.attributes, cellLevel(cell));
      const vit = vitality ? vitality(cellIndex(gx, gy)) : 1.0;
      for (const key of ATTRIBUTE_KEYS) {
        const v = leveled[key];
        if (v != null) out[key] += w * v * (vit < 1 ? vit : 1);
      }
    }
  }
  return out;
}
